/**
 * Scoring Rules — Client-Side Heuristic Only
 *
 * IMPORTANT: These rules are used ONLY for founder self-assessment
 * (quick readiness estimate during onboarding). They contain
 * simplified, common-sense thresholds — NOT the proprietary
 * investor scoring methodology.
 *
 * The real scoring methodology (weights, rubrics, sub-criteria
 * definitions, confidence multipliers) lives in the server-side v2/v2.1
 * methodology bundle (`_shared/scoring/methodology/`) and is computed via
 * the `ai-score` / `score-startup` edge functions (`runScoring()` →
 * `score_event`).
 */

import type { StartupProfile } from '@/lib/state/profile-store';
import type { ScoringDimension, EvidenceLevel } from './methodology';

// =============================================================================
// TYPES
// =============================================================================

export interface ScoringRule {
  dimension: ScoringDimension;
  subCriterionId: string;
  evaluate: (profile: StartupProfile) => number; // Returns 0-10 score
  getEvidence: (profile: StartupProfile) => EvidenceLevel;
  getFeedback: (profile: StartupProfile, score: number) => string;
}

export interface SubCriterionResult {
  criterionId: string;
  score: number;
  maxScore: number;
  evidence: EvidenceLevel;
  feedback: string;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Normalize a value to 0-10 scale with thresholds
 */
function normalizeScore(value: number, thresholds: { low: number; mid: number; high: number }): number {
  if (value >= thresholds.high) return 10;
  if (value >= thresholds.mid) return 7 + ((value - thresholds.mid) / (thresholds.high - thresholds.mid)) * 3;
  if (value >= thresholds.low) return 4 + ((value - thresholds.low) / (thresholds.mid - thresholds.low)) * 3;
  if (value > 0) return 1 + (value / thresholds.low) * 3;
  return 1;
}

/**
 * Check if a text field has meaningful content
 */
function hasContent(text: string | undefined): boolean {
  return !!text && text.trim().length > 20;
}

/**
 * Check for keywords in text
 */
function hasKeywords(text: string | undefined, keywords: string[]): number {
  if (!text) return 0;
  const lowerText = text.toLowerCase();
  return keywords.filter((kw) => lowerText.includes(kw.toLowerCase())).length;
}

/**
 * Determine evidence level based on data presence
 */
function getDataEvidence(hasData: boolean, isVerifiable: boolean): EvidenceLevel {
  if (!hasData) return 'missing';
  if (isVerifiable) return 'self-declared';
  return 'self-declared';
}

/**
 * Industry-based growth scoring (AI, FinTech, HealthTech = high growth sectors)
 */
const HIGH_GROWTH_INDUSTRIES = ['ai', 'ml', 'machine learning', 'artificial intelligence', 'fintech', 'healthtech', 'saas', 'enterprise saas'];

function isHighGrowthIndustry(industry: string | undefined): boolean {
  if (!industry) return false;
  return HIGH_GROWTH_INDUSTRIES.some((hg) => industry.toLowerCase().includes(hg));
}

// =============================================================================
// TEAM DIMENSION RULES
// =============================================================================

const teamFounderExperienceRule: ScoringRule = {
  dimension: 'team',
  subCriterionId: 'team-founder-experience',
  evaluate: (profile) => {
    let score = 4; // Base score for first-time founder

    // Serial founder bonus
    if (profile.isSerialFounder) {
      score += 3;
    }

    // Previous ventures bonus
    const ventureCount = profile.previousVentures?.length ?? 0;
    if (ventureCount >= 2) {
      score += 2;
    } else if (ventureCount === 1) {
      score += 1;
    }

    // Industry experience indicator from description
    if (hasContent(profile.description)) {
      score += 0.5;
    }

    return Math.min(10, Math.max(1, score));
  },
  getEvidence: (profile) => {
    if (profile.isSerialFounder || (profile.previousVentures && profile.previousVentures.length > 0)) {
      return 'self-declared';
    }
    return 'missing';
  },
  getFeedback: (profile, score) => {
    if (score >= 8) return 'Serial entrepreneur with multiple ventures';
    if (score >= 6) return 'Previous startup experience or relevant background';
    if (score >= 4) return 'First-time founder with some relevant experience';
    return 'Limited relevant experience documented';
  },
};

const teamCompletenessRule: ScoringRule = {
  dimension: 'team',
  subCriterionId: 'team-completeness',
  evaluate: (profile) => {
    const teamSize = profile.teamSize ?? 1;
    const stage = profile.stage;

    // Stage-specific team size expectations
    const expectations: Record<string, { ideal: number; min: number }> = {
      'pre-seed': { ideal: 3, min: 1 },
      seed: { ideal: 6, min: 3 },
      'series-a': { ideal: 15, min: 8 },
    };

    const exp = expectations[stage] ?? expectations['seed'];

    if (teamSize >= exp.ideal) return 9;
    if (teamSize >= exp.min) return 5 + ((teamSize - exp.min) / (exp.ideal - exp.min)) * 4;
    if (teamSize > 1) return 4;
    return 2; // Solo founder
  },
  getEvidence: (profile) => {
    return profile.teamSize ? 'self-declared' : 'missing';
  },
  getFeedback: (profile, score) => {
    const teamSize = profile.teamSize ?? 1;
    if (score >= 8) return `Strong team of ${teamSize} members for ${profile.stage} stage`;
    if (score >= 5) return `Core team building (${teamSize} members), some key roles may be needed`;
    if (teamSize <= 1) return 'Solo founder - consider hiring key roles';
    return `Small team (${teamSize} members) for current stage`;
  },
};

const teamDomainExpertiseRule: ScoringRule = {
  dimension: 'team',
  subCriterionId: 'team-domain-expertise',
  evaluate: (profile) => {
    let score = 5; // Base score

    // Check problem statement quality
    if (hasContent(profile.problem)) {
      score += 1.5;
      // Check for depth indicators
      const problemKeywords = ['years', 'experience', 'worked', 'built', 'led', 'managed'];
      if (hasKeywords(profile.problem, problemKeywords) > 0) {
        score += 1;
      }
    }

    // Check solution description quality
    if (hasContent(profile.solution)) {
      score += 1;
    }

    // Check description for domain signals
    if (hasContent(profile.description)) {
      score += 1;
    }

    return Math.min(10, Math.max(1, score));
  },
  getEvidence: (profile) => {
    if (hasContent(profile.problem) || hasContent(profile.solution)) {
      return 'self-declared';
    }
    return 'missing';
  },
  getFeedback: (profile, score) => {
    if (score >= 8) return 'Deep domain understanding evident from problem/solution clarity';
    if (score >= 6) return 'Good domain knowledge demonstrated';
    if (score >= 4) return 'Some domain understanding, could articulate more clearly';
    return 'Limited domain expertise documented';
  },
};

const teamCoachabilityRule: ScoringRule = {
  dimension: 'team',
  subCriterionId: 'team-coachability',
  evaluate: () => {
    // Default mid-range score (requires investor assessment)
    return 6;
  },
  getEvidence: () => 'missing',
  getFeedback: () => 'Coachability assessment requires investor interaction',
};

// =============================================================================
// PRODUCT DIMENSION RULES
// =============================================================================

const productStageRule: ScoringRule = {
  dimension: 'product',
  subCriterionId: 'product-stage',
  evaluate: (profile) => {
    const stage = profile.stage;
    const userCount = profile.userCount ?? 0;
    const revenue = profile.monthlyRevenue ?? 0;

    // Base score from stage
    let score = 4;

    // Revenue indicates product in market
    if (revenue > 0) {
      score = 7;
      if (revenue >= 10000) score = 8;
      if (revenue >= 50000) score = 9;
      if (revenue >= 100000) score = 10;
    } else if (userCount > 0) {
      // Users without revenue = beta/free
      score = 5;
      if (userCount >= 100) score = 6;
      if (userCount >= 1000) score = 7;
    }

    // Stage bump
    if (stage === 'series-a') score = Math.max(score, 7);
    if (stage === 'seed') score = Math.max(score, 5);

    return Math.min(10, score);
  },
  getEvidence: (profile) => {
    if (profile.monthlyRevenue && profile.monthlyRevenue > 0) return 'self-declared';
    if (profile.userCount && profile.userCount > 0) return 'self-declared';
    return 'missing';
  },
  getFeedback: (profile, score) => {
    const revenue = profile.monthlyRevenue ?? 0;
    const users = profile.userCount ?? 0;

    if (score >= 9) return `Product in market with strong revenue ($${revenue.toLocaleString()}/mo)`;
    if (score >= 7) return `Product with paying customers (${revenue > 0 ? `$${revenue.toLocaleString()}/mo` : `${users} users`})`;
    if (score >= 5) return `Beta product with ${users > 0 ? `${users} users` : 'initial traction'}`;
    return 'Early product stage - concept or prototype';
  },
};

const productQualityRule: ScoringRule = {
  dimension: 'product',
  subCriterionId: 'product-quality',
  evaluate: () => {
    // Default mid-range (requires demo/review)
    return 5;
  },
  getEvidence: () => 'missing',
  getFeedback: () => 'Product quality assessment requires demo or user feedback data',
};

const productDifferentiationRule: ScoringRule = {
  dimension: 'product',
  subCriterionId: 'product-differentiation',
  evaluate: (profile) => {
    let score = 4;

    // Check for differentiation keywords in description/solution
    const diffKeywords = ['proprietary', 'unique', 'patent', 'first', 'only', 'revolutionary', 'breakthrough', 'innovative'];
    const techKeywords = ['ai', 'ml', 'machine learning', 'algorithm', 'platform', 'api'];

    const diffCount = hasKeywords(profile.description, diffKeywords) + hasKeywords(profile.solution, diffKeywords);
    const techCount = hasKeywords(profile.description, techKeywords) + hasKeywords(profile.solution, techKeywords);

    score += Math.min(3, diffCount);
    score += Math.min(2, techCount * 0.5);

    return Math.min(10, Math.max(1, score));
  },
  getEvidence: (profile) => {
    if (hasContent(profile.solution) || hasContent(profile.description)) {
      return 'self-declared';
    }
    return 'missing';
  },
  getFeedback: (profile, score) => {
    if (score >= 8) return 'Strong technical differentiation indicated';
    if (score >= 6) return 'Notable technical advantages present';
    if (score >= 4) return 'Some differentiation, may need stronger positioning';
    return 'Limited differentiation documented';
  },
};

const productRoadmapRule: ScoringRule = {
  dimension: 'product',
  subCriterionId: 'product-roadmap',
  evaluate: () => {
    // Default mid-range (requires doc upload)
    return 5;
  },
  getEvidence: () => 'missing',
  getFeedback: () => 'Product roadmap assessment requires document upload',
};

// =============================================================================
// MARKET DIMENSION RULES
// =============================================================================

const marketSizeRule: ScoringRule = {
  dimension: 'market',
  subCriterionId: 'market-size',
  evaluate: (profile) => {
    const marketSize = profile.marketSize ?? 0;

    // Thresholds in USD
    if (marketSize >= 10_000_000_000) return 10; // $10B+
    if (marketSize >= 1_000_000_000) return 7 + ((marketSize - 1_000_000_000) / 9_000_000_000) * 3; // $1B - $10B
    if (marketSize >= 500_000_000) return 5 + ((marketSize - 500_000_000) / 500_000_000) * 2; // $500M - $1B
    if (marketSize >= 100_000_000) return 3 + ((marketSize - 100_000_000) / 400_000_000) * 2; // $100M - $500M
    if (marketSize > 0) return 2;
    return 4; // Unknown = mid default
  },
  getEvidence: (profile) => {
    return profile.marketSize ? 'self-declared' : 'missing';
  },
  getFeedback: (profile, score) => {
    const size = profile.marketSize ?? 0;
    if (score >= 9) return `Large TAM ($${(size / 1_000_000_000).toFixed(1)}B+)`;
    if (score >= 7) return `Strong market opportunity ($${(size / 1_000_000_000).toFixed(1)}B TAM)`;
    if (score >= 5) return `Solid market size ($${(size / 1_000_000).toFixed(0)}M TAM)`;
    if (size > 0) return `Niche market ($${(size / 1_000_000).toFixed(0)}M TAM)`;
    return 'Market size not documented';
  },
};

const marketGrowthRule: ScoringRule = {
  dimension: 'market',
  subCriterionId: 'market-growth',
  evaluate: (profile) => {
    // Infer from industry
    if (isHighGrowthIndustry(profile.industry)) {
      return 8;
    }
    // Check for growth keywords
    const growthKeywords = ['growing', 'emerging', 'rapid', 'accelerating', 'booming'];
    if (hasKeywords(profile.description, growthKeywords) > 0) {
      return 7;
    }
    return 5; // Default mid-range
  },
  getEvidence: (profile) => {
    return profile.industry ? 'self-declared' : 'missing';
  },
  getFeedback: (profile, score) => {
    if (score >= 8) return `High-growth sector: ${profile.industry}`;
    if (score >= 6) return 'Growing market with positive tailwinds';
    return 'Market growth rate not specifically documented';
  },
};

const marketDynamicsRule: ScoringRule = {
  dimension: 'market',
  subCriterionId: 'market-dynamics',
  evaluate: (profile) => {
    let score = 5;

    // Check target customer clarity
    if (hasContent(profile.targetCustomer)) {
      score += 2;
      // Check for B2B indicators (typically clearer buyer)
      const b2bKeywords = ['enterprise', 'business', 'companies', 'b2b', 'saas', 'teams'];
      if (hasKeywords(profile.targetCustomer, b2bKeywords) > 0) {
        score += 1;
      }
    }

    return Math.min(10, score);
  },
  getEvidence: (profile) => {
    return hasContent(profile.targetCustomer) ? 'self-declared' : 'missing';
  },
  getFeedback: (profile, score) => {
    if (score >= 8) return 'Clear buyer identified with favorable dynamics';
    if (score >= 6) return 'Target customer defined';
    return 'Market dynamics need more clarity';
  },
};

const marketPositioningRule: ScoringRule = {
  dimension: 'market',
  subCriterionId: 'market-positioning',
  evaluate: (profile) => {
    let score = 4;

    // Tagline quality
    if (profile.tagline && profile.tagline.length >= 10 && profile.tagline.length <= 100) {
      score += 2;
    }

    // Target customer clarity
    if (hasContent(profile.targetCustomer)) {
      score += 2;
    }

    // Industry specificity
    if (profile.industry && profile.industry.length > 3) {
      score += 1;
    }

    return Math.min(10, score);
  },
  getEvidence: (profile) => {
    if (profile.tagline && hasContent(profile.targetCustomer)) {
      return 'self-declared';
    }
    return 'missing';
  },
  getFeedback: (profile, score) => {
    if (score >= 8) return 'Clear positioning and target segment';
    if (score >= 6) return 'Good positioning, defined target market';
    if (score >= 4) return 'Positioning could be more specific';
    return 'Market positioning unclear';
  },
};

// =============================================================================
// TRACTION DIMENSION RULES
// =============================================================================

const tractionRevenueRule: ScoringRule = {
  dimension: 'traction',
  subCriterionId: 'traction-revenue',
  evaluate: (profile) => {
    const mrr = profile.monthlyRevenue ?? 0;
    const stage = profile.stage;

    // Stage-specific thresholds
    const thresholds: Record<string, { low: number; mid: number; high: number }> = {
      'pre-seed': { low: 1000, mid: 5000, high: 20000 },
      seed: { low: 5000, mid: 20000, high: 100000 },
      'series-a': { low: 50000, mid: 100000, high: 500000 },
    };

    const th = thresholds[stage] ?? thresholds['seed'];
    return normalizeScore(mrr, th);
  },
  getEvidence: (profile) => {
    return profile.monthlyRevenue && profile.monthlyRevenue > 0 ? 'self-declared' : 'missing';
  },
  getFeedback: (profile, score) => {
    const mrr = profile.monthlyRevenue ?? 0;
    if (mrr === 0) return 'No revenue reported yet';
    if (score >= 8) return `Strong MRR: $${mrr.toLocaleString()}/mo`;
    if (score >= 5) return `Growing revenue: $${mrr.toLocaleString()}/mo`;
    return `Early revenue: $${mrr.toLocaleString()}/mo`;
  },
};

const tractionUsersRule: ScoringRule = {
  dimension: 'traction',
  subCriterionId: 'traction-users',
  evaluate: (profile) => {
    const users = profile.userCount ?? 0;
    const stage = profile.stage;

    // Stage-specific thresholds
    const thresholds: Record<string, { low: number; mid: number; high: number }> = {
      'pre-seed': { low: 50, mid: 200, high: 1000 },
      seed: { low: 200, mid: 1000, high: 5000 },
      'series-a': { low: 1000, mid: 5000, high: 20000 },
    };

    const th = thresholds[stage] ?? thresholds['seed'];
    return normalizeScore(users, th);
  },
  getEvidence: (profile) => {
    return profile.userCount && profile.userCount > 0 ? 'self-declared' : 'missing';
  },
  getFeedback: (profile, score) => {
    const users = profile.userCount ?? 0;
    if (users === 0) return 'No user count reported';
    if (score >= 8) return `Strong user base: ${users.toLocaleString()} users`;
    if (score >= 5) return `Growing users: ${users.toLocaleString()}`;
    return `Early users: ${users.toLocaleString()}`;
  },
};

const tractionEngagementRule: ScoringRule = {
  dimension: 'traction',
  subCriterionId: 'traction-engagement',
  evaluate: () => {
    // Default mid-range (requires engagement metrics)
    return 5;
  },
  getEvidence: () => 'missing',
  getFeedback: () => 'Engagement metrics require detailed analytics data',
};

const tractionPmfRule: ScoringRule = {
  dimension: 'traction',
  subCriterionId: 'traction-pmf',
  evaluate: (profile) => {
    const growthRate = profile.growthRate ?? 0;

    // Growth rate as PMF indicator
    if (growthRate >= 30) return 9;
    if (growthRate >= 20) return 7;
    if (growthRate >= 10) return 5;
    if (growthRate >= 5) return 4;
    if (growthRate > 0) return 3;
    return 4; // No data = neutral
  },
  getEvidence: (profile) => {
    return profile.growthRate !== undefined ? 'self-declared' : 'missing';
  },
  getFeedback: (profile, score) => {
    const growth = profile.growthRate ?? 0;
    if (score >= 8) return `Strong PMF signals with ${growth}% growth`;
    if (score >= 6) return `Good PMF indicators, ${growth}% growth`;
    if (score >= 4) return 'Early PMF signals, iterating based on feedback';
    return 'No clear PMF indicators documented';
  },
};

// =============================================================================
// DEFENSIBILITY DIMENSION RULES
// =============================================================================

const defensibilityIpRule: ScoringRule = {
  dimension: 'defensibility',
  subCriterionId: 'defensibility-ip',
  evaluate: () => 5, // Default mid-range (requires IP docs)
  getEvidence: () => 'missing',
  getFeedback: () => 'IP protection assessment requires document upload',
};

const defensibilityNetworkRule: ScoringRule = {
  dimension: 'defensibility',
  subCriterionId: 'defensibility-network',
  evaluate: () => 5, // Default mid-range
  getEvidence: () => 'missing',
  getFeedback: () => 'Network effects assessment requires detailed business model review',
};

const defensibilitySwitchingRule: ScoringRule = {
  dimension: 'defensibility',
  subCriterionId: 'defensibility-switching',
  evaluate: () => 5, // Default mid-range
  getEvidence: () => 'missing',
  getFeedback: () => 'Switching costs assessment requires product/customer data',
};

const defensibilityBrandRule: ScoringRule = {
  dimension: 'defensibility',
  subCriterionId: 'defensibility-brand',
  evaluate: () => 5, // Default mid-range
  getEvidence: () => 'missing',
  getFeedback: () => 'Brand strength assessment requires market presence data',
};

// =============================================================================
// COMPETITION DIMENSION RULES
// =============================================================================

const competitionLandscapeRule: ScoringRule = {
  dimension: 'competition',
  subCriterionId: 'competition-landscape',
  evaluate: (profile) => {
    const competitorCount = profile.competitors?.length ?? 0;

    // Fewer competitors = better (blue ocean)
    if (competitorCount === 0) return 6; // Unknown = neutral
    if (competitorCount <= 2) return 8; // Limited competition
    if (competitorCount <= 5) return 6; // Manageable
    if (competitorCount <= 10) return 4; // Crowded
    return 3; // Very crowded
  },
  getEvidence: (profile) => {
    return profile.competitors && profile.competitors.length > 0 ? 'self-declared' : 'missing';
  },
  getFeedback: (profile, score) => {
    const count = profile.competitors?.length ?? 0;
    if (count === 0) return 'Competitive landscape not documented';
    if (score >= 8) return `Limited competition (${count} competitors)`;
    if (score >= 6) return `Manageable competitive landscape (${count} competitors)`;
    return `Crowded market (${count} competitors)`;
  },
};

const competitionDifferentiationRule: ScoringRule = {
  dimension: 'competition',
  subCriterionId: 'competition-differentiation',
  evaluate: (profile) => {
    let score = 5;

    // Check solution for differentiation claims
    const diffKeywords = ['better', 'faster', 'cheaper', 'unique', 'only', '10x', 'first'];
    const diffCount = hasKeywords(profile.solution, diffKeywords);
    score += Math.min(3, diffCount);

    // Technical differentiation
    const techKeywords = ['ai', 'proprietary', 'patent', 'algorithm'];
    const techCount = hasKeywords(profile.solution, techKeywords) + hasKeywords(profile.description, techKeywords);
    score += Math.min(2, techCount);

    return Math.min(10, score);
  },
  getEvidence: (profile) => {
    return hasContent(profile.solution) ? 'self-declared' : 'missing';
  },
  getFeedback: (profile, score) => {
    if (score >= 8) return 'Strong differentiation vs competitors';
    if (score >= 6) return 'Clear advantages in specific areas';
    if (score >= 4) return 'Some differentiation, could strengthen positioning';
    return 'Differentiation unclear from available data';
  },
};

const competitionResponseRule: ScoringRule = {
  dimension: 'competition',
  subCriterionId: 'competition-response',
  evaluate: () => 5, // Default mid-range
  getEvidence: () => 'missing',
  getFeedback: () => 'Competitive response risk requires market analysis',
};

// =============================================================================
// BUSINESS MODEL DIMENSION RULES
// =============================================================================

const bmRevenueModelRule: ScoringRule = {
  dimension: 'business-model',
  subCriterionId: 'bm-revenue-model',
  evaluate: (profile) => {
    // Revenue existence indicates proven model
    const hasRevenue = (profile.monthlyRevenue ?? 0) > 0;

    if (hasRevenue) {
      const mrr = profile.monthlyRevenue ?? 0;
      if (mrr >= 50000) return 9;
      if (mrr >= 10000) return 7;
      return 6;
    }

    // Check for SaaS indicators
    if (isHighGrowthIndustry(profile.industry)) {
      return 5;
    }

    return 4;
  },
  getEvidence: (profile) => {
    return (profile.monthlyRevenue ?? 0) > 0 ? 'self-declared' : 'missing';
  },
  getFeedback: (profile, score) => {
    const hasRevenue = (profile.monthlyRevenue ?? 0) > 0;
    if (score >= 8) return 'Proven revenue model with strong traction';
    if (score >= 6) return 'Revenue model validated with paying customers';
    if (hasRevenue) return 'Revenue model in early validation';
    return 'Revenue model not yet proven';
  },
};

const bmUnitEconomicsRule: ScoringRule = {
  dimension: 'business-model',
  subCriterionId: 'bm-unit-economics',
  evaluate: () => 5, // Default mid-range (requires financial docs)
  getEvidence: () => 'missing',
  getFeedback: () => 'Unit economics assessment requires financial documentation',
};

const bmProfitabilityRule: ScoringRule = {
  dimension: 'business-model',
  subCriterionId: 'bm-profitability',
  evaluate: () => 5, // Default mid-range
  getEvidence: () => 'missing',
  getFeedback: () => 'Profitability path requires financial projections',
};

const bmScalabilityRule: ScoringRule = {
  dimension: 'business-model',
  subCriterionId: 'bm-scalability',
  evaluate: (profile) => {
    // SaaS and tech = high scalability
    if (isHighGrowthIndustry(profile.industry)) {
      return 7;
    }

    const scalableKeywords = ['saas', 'platform', 'software', 'digital', 'cloud', 'api'];
    if (hasKeywords(profile.industry, scalableKeywords) > 0 || hasKeywords(profile.description, scalableKeywords) > 0) {
      return 7;
    }

    return 5;
  },
  getEvidence: (profile) => {
    return profile.industry ? 'self-declared' : 'missing';
  },
  getFeedback: (profile, score) => {
    if (score >= 7) return 'Highly scalable business model';
    if (score >= 5) return 'Good scalability potential';
    return 'Scalability requires further analysis';
  },
};

// =============================================================================
// TIMING DIMENSION RULES
// =============================================================================

const timingWhyNowRule: ScoringRule = {
  dimension: 'timing',
  subCriterionId: 'timing-why-now',
  evaluate: (profile) => {
    // Check for timing keywords
    const timingKeywords = ['now', 'moment', 'trend', 'shift', 'change', 'covid', 'pandemic', 'remote', 'digital transformation'];
    const keywordCount = hasKeywords(profile.description, timingKeywords) + hasKeywords(profile.problem, timingKeywords);

    if (keywordCount >= 2) return 8;
    if (keywordCount >= 1) return 6;

    // Industry timing
    if (isHighGrowthIndustry(profile.industry)) {
      return 7;
    }

    return 5;
  },
  getEvidence: (profile) => {
    return hasContent(profile.description) ? 'self-declared' : 'missing';
  },
  getFeedback: (profile, score) => {
    if (score >= 8) return 'Clear timing catalyst identified';
    if (score >= 6) return 'Good timing factors present';
    return 'Timing rationale could be strengthened';
  },
};

const timingTailwindsRule: ScoringRule = {
  dimension: 'timing',
  subCriterionId: 'timing-tailwinds',
  evaluate: (profile) => {
    // High-growth industries have strong tailwinds
    if (isHighGrowthIndustry(profile.industry)) {
      return 8;
    }

    // Check for tailwind keywords
    const tailwindKeywords = ['growing', 'emerging', 'trend', 'adoption', 'transformation', 'shift'];
    if (hasKeywords(profile.description, tailwindKeywords) > 0) {
      return 7;
    }

    return 5;
  },
  getEvidence: (profile) => {
    return profile.industry ? 'self-declared' : 'missing';
  },
  getFeedback: (profile, score) => {
    if (score >= 8) return `Strong tailwinds in ${profile.industry}`;
    if (score >= 6) return 'Favorable market trends';
    return 'Market tailwinds not specifically documented';
  },
};

// =============================================================================
// VISION & STORY DIMENSION RULES
// =============================================================================

const visionClarityRule: ScoringRule = {
  dimension: 'vision-story',
  subCriterionId: 'vision-clarity',
  evaluate: (profile) => {
    let score = 4;

    // Tagline quality
    if (profile.tagline && profile.tagline.length >= 10) {
      score += 2;
    }

    // Description depth
    if (hasContent(profile.description)) {
      score += 1;
      if (profile.description && profile.description.length > 100) {
        score += 1;
      }
    }

    // Problem clarity
    if (hasContent(profile.problem)) {
      score += 1;
    }

    return Math.min(10, score);
  },
  getEvidence: (profile) => {
    if (profile.tagline && hasContent(profile.description)) {
      return 'self-declared';
    }
    return 'missing';
  },
  getFeedback: (profile, score) => {
    if (score >= 8) return 'Clear, compelling vision articulated';
    if (score >= 6) return 'Good vision clarity';
    if (score >= 4) return 'Vision could be more clearly defined';
    return 'Vision statement needs development';
  },
};

const visionStorytellingRule: ScoringRule = {
  dimension: 'vision-story',
  subCriterionId: 'vision-storytelling',
  evaluate: () => 5, // Default mid-range (requires pitch deck)
  getEvidence: () => 'missing',
  getFeedback: () => 'Storytelling assessment requires pitch deck review',
};

// =============================================================================
// GTM EXECUTION DIMENSION RULES
// =============================================================================

const gtmStrategyRule: ScoringRule = {
  dimension: 'gtm-execution',
  subCriterionId: 'gtm-strategy',
  evaluate: () => 5, // Default mid-range
  getEvidence: () => 'missing',
  getFeedback: () => 'GTM strategy requires documented plan',
};

const gtmSalesRule: ScoringRule = {
  dimension: 'gtm-execution',
  subCriterionId: 'gtm-sales',
  evaluate: (profile) => {
    // Infer from user growth and revenue
    const hasUsers = (profile.userCount ?? 0) > 0;
    const hasRevenue = (profile.monthlyRevenue ?? 0) > 0;

    if (hasRevenue && hasUsers) return 6;
    if (hasUsers) return 5;
    return 4;
  },
  getEvidence: (profile) => {
    return (profile.userCount ?? 0) > 0 ? 'self-declared' : 'missing';
  },
  getFeedback: (profile, score) => {
    if (score >= 6) return 'Sales capability demonstrated through traction';
    if (score >= 5) return 'Early sales efforts showing results';
    return 'Sales capability not yet demonstrated';
  },
};

const gtmMarketingRule: ScoringRule = {
  dimension: 'gtm-execution',
  subCriterionId: 'gtm-marketing',
  evaluate: () => 5, // Default mid-range
  getEvidence: () => 'missing',
  getFeedback: () => 'Marketing effectiveness requires analytics data',
};

// =============================================================================
// FINANCIAL HEALTH DIMENSION RULES
// =============================================================================

const finRunwayRule: ScoringRule = {
  dimension: 'financial-health',
  subCriterionId: 'fin-runway',
  evaluate: (profile) => {
    // Infer from funding target relative to stage
    const fundingTarget = profile.fundingTarget ?? 0;
    const stage = profile.stage;

    // Typical burn assumptions by stage
    const burnAssumptions: Record<string, number> = {
      'pre-seed': 30000,
      seed: 60000,
      'series-a': 150000,
    };

    const monthlyBurn = burnAssumptions[stage] ?? 50000;

    // Estimate runway if they raise target
    const estimatedRunway = fundingTarget / monthlyBurn;

    if (estimatedRunway >= 18) return 9;
    if (estimatedRunway >= 12) return 7;
    if (estimatedRunway >= 6) return 5;
    return 4;
  },
  getEvidence: (profile) => {
    return profile.fundingTarget ? 'self-declared' : 'missing';
  },
  getFeedback: (profile, score) => {
    if (score >= 8) return 'Strong runway potential with current raise';
    if (score >= 6) return 'Adequate runway with raise target';
    return 'Runway concerns - consider raising more or reducing burn';
  },
};

const finCapitalEfficiencyRule: ScoringRule = {
  dimension: 'financial-health',
  subCriterionId: 'fin-capital-efficiency',
  evaluate: (profile) => {
    const mrr = profile.monthlyRevenue ?? 0;
    const stage = profile.stage;

    // Stage funding norms
    const fundingNorms: Record<string, number> = {
      'pre-seed': 300000,
      seed: 1500000,
      'series-a': 6000000,
    };

    const typicalRaised = fundingNorms[stage] ?? 1000000;
    const annualRevenue = mrr * 12;

    // ARR per dollar raised
    const efficiency = annualRevenue / typicalRaised;

    if (efficiency >= 1) return 10;
    if (efficiency >= 0.5) return 7;
    if (efficiency >= 0.25) return 5;
    if (efficiency > 0) return 3;
    return 4; // No revenue = neutral
  },
  getEvidence: (profile) => {
    return (profile.monthlyRevenue ?? 0) > 0 ? 'self-declared' : 'missing';
  },
  getFeedback: (profile, score) => {
    if (score >= 8) return 'Excellent capital efficiency';
    if (score >= 5) return 'Good capital efficiency for stage';
    return 'Capital efficiency metrics need improvement';
  },
};

const finHygieneRule: ScoringRule = {
  dimension: 'financial-health',
  subCriterionId: 'fin-hygiene',
  evaluate: () => 5, // Default mid-range (requires financial docs)
  getEvidence: () => 'missing',
  getFeedback: () => 'Financial hygiene assessment requires financial documentation',
};

// =============================================================================
// EXECUTION VELOCITY DIMENSION RULES
// =============================================================================

const execSpeedRule: ScoringRule = {
  dimension: 'execution-velocity',
  subCriterionId: 'exec-speed',
  evaluate: (profile) => {
    const foundedYear = profile.foundedYear;
    const stage = profile.stage;
    const currentYear = new Date().getFullYear();

    const yearsOld = currentYear - foundedYear;

    // Expected progression (years to reach stage)
    const expectedYears: Record<string, number> = {
      'pre-seed': 1,
      seed: 2,
      'series-a': 3,
    };

    const expected = expectedYears[stage] ?? 2;

    if (yearsOld <= expected) return 8; // Fast
    if (yearsOld <= expected + 1) return 6; // Normal
    if (yearsOld <= expected + 2) return 4; // Slow
    return 3; // Very slow
  },
  getEvidence: (profile) => {
    return profile.foundedYear ? 'self-declared' : 'missing';
  },
  getFeedback: (profile, score) => {
    const age = new Date().getFullYear() - profile.foundedYear;
    if (score >= 8) return `Fast execution - ${profile.stage} in ${age} year(s)`;
    if (score >= 6) return `Good execution pace for ${profile.stage}`;
    return `Consider accelerating execution velocity`;
  },
};

const execMilestonesRule: ScoringRule = {
  dimension: 'execution-velocity',
  subCriterionId: 'exec-milestones',
  evaluate: () => 5, // Default mid-range
  getEvidence: () => 'missing',
  getFeedback: () => 'Milestone achievement requires historical data',
};

// =============================================================================
// SCALABILITY DIMENSION RULES
// =============================================================================

const scaleTechRule: ScoringRule = {
  dimension: 'scalability',
  subCriterionId: 'scale-tech',
  evaluate: () => 5, // Default mid-range (requires tech docs)
  getEvidence: () => 'missing',
  getFeedback: () => 'Technical scalability requires architecture documentation',
};

const scaleOpsRule: ScoringRule = {
  dimension: 'scalability',
  subCriterionId: 'scale-ops',
  evaluate: () => 5, // Default mid-range
  getEvidence: () => 'missing',
  getFeedback: () => 'Operational scalability requires process documentation',
};

const scaleTeamRule: ScoringRule = {
  dimension: 'scalability',
  subCriterionId: 'scale-team',
  evaluate: (profile) => {
    const teamSize = profile.teamSize ?? 1;
    const stage = profile.stage;

    // Stage norms
    const teamNorms: Record<string, { min: number; ideal: number }> = {
      'pre-seed': { min: 2, ideal: 4 },
      seed: { min: 4, ideal: 10 },
      'series-a': { min: 10, ideal: 25 },
    };

    const norm = teamNorms[stage] ?? teamNorms['seed'];

    if (teamSize >= norm.ideal) return 8;
    if (teamSize >= norm.min) return 6;
    return 4;
  },
  getEvidence: (profile) => {
    return profile.teamSize ? 'self-declared' : 'missing';
  },
  getFeedback: (profile, score) => {
    if (score >= 8) return `Strong team size (${profile.teamSize}) for growth`;
    if (score >= 6) return `Team growing appropriately for ${profile.stage}`;
    return 'Team scalability may be a constraint';
  },
};

// =============================================================================
// DEAL ATTRACTIVENESS DIMENSION RULES
// =============================================================================

const dealTermsRule: ScoringRule = {
  dimension: 'deal-attractiveness',
  subCriterionId: 'deal-terms-factor',
  evaluate: () => 5, // Default mid-range (requires term sheet)
  getEvidence: () => 'missing',
  getFeedback: () => 'Deal terms assessment requires term sheet and cap table',
};

const dealExitCompsRule: ScoringRule = {
  dimension: 'deal-attractiveness',
  subCriterionId: 'deal-exit-comps',
  evaluate: () => 5, // Default mid-range
  getEvidence: () => 'missing',
  getFeedback: () => 'Exit comparables require financial analysis',
};

// =============================================================================
// SOCIAL PROOF DIMENSION RULES
// =============================================================================

const socialInvestorQualityRule: ScoringRule = {
  dimension: 'social-proof',
  subCriterionId: 'social-investor-quality',
  evaluate: () => 5, // Default mid-range
  getEvidence: () => 'missing',
  getFeedback: () => 'Investor quality assessment requires cap table and investor details',
};

const socialAcceleratorRule: ScoringRule = {
  dimension: 'social-proof',
  subCriterionId: 'social-accelerator-tier',
  evaluate: () => 5, // Default mid-range
  getEvidence: () => 'missing',
  getFeedback: () => 'Accelerator participation requires documentation',
};

const socialCompetitionWinsRule: ScoringRule = {
  dimension: 'social-proof',
  subCriterionId: 'social-competition-wins',
  evaluate: () => 5, // Default mid-range
  getEvidence: () => 'missing',
  getFeedback: () => 'Competition results require documentation',
};

const socialEndorsementsRule: ScoringRule = {
  dimension: 'social-proof',
  subCriterionId: 'social-strategic-endorsements',
  evaluate: () => 5, // Default mid-range
  getEvidence: () => 'missing',
  getFeedback: () => 'Strategic endorsements require partnership documentation',
};

// =============================================================================
// EXPORTED RULES AND FUNCTIONS
// =============================================================================

/**
 * All scoring rules organized by dimension
 */
export const SCORING_RULES: ScoringRule[] = [
  // Team
  teamFounderExperienceRule,
  teamCompletenessRule,
  teamDomainExpertiseRule,
  teamCoachabilityRule,
  // Product
  productStageRule,
  productQualityRule,
  productDifferentiationRule,
  productRoadmapRule,
  // Market
  marketSizeRule,
  marketGrowthRule,
  marketDynamicsRule,
  marketPositioningRule,
  // Traction
  tractionRevenueRule,
  tractionUsersRule,
  tractionEngagementRule,
  tractionPmfRule,
  // Defensibility
  defensibilityIpRule,
  defensibilityNetworkRule,
  defensibilitySwitchingRule,
  defensibilityBrandRule,
  // Competition
  competitionLandscapeRule,
  competitionDifferentiationRule,
  competitionResponseRule,
  // Business Model
  bmRevenueModelRule,
  bmUnitEconomicsRule,
  bmProfitabilityRule,
  bmScalabilityRule,
  // Timing
  timingWhyNowRule,
  timingTailwindsRule,
  // Vision & Story
  visionClarityRule,
  visionStorytellingRule,
  // GTM Execution
  gtmStrategyRule,
  gtmSalesRule,
  gtmMarketingRule,
  // Financial Health
  finRunwayRule,
  finCapitalEfficiencyRule,
  finHygieneRule,
  // Execution Velocity
  execSpeedRule,
  execMilestonesRule,
  // Scalability
  scaleTechRule,
  scaleOpsRule,
  scaleTeamRule,
  // Deal Attractiveness
  dealTermsRule,
  dealExitCompsRule,
  // Social Proof
  socialInvestorQualityRule,
  socialAcceleratorRule,
  socialCompetitionWinsRule,
  socialEndorsementsRule,
];

/**
 * Get rules for a specific dimension
 */
export function getRulesForDimension(dimension: ScoringDimension): ScoringRule[] {
  return SCORING_RULES.filter((rule) => rule.dimension === dimension);
}

/**
 * Evaluate a single sub-criterion
 */
export function evaluateSubCriterion(rule: ScoringRule, profile: StartupProfile): SubCriterionResult {
  const score = Math.round(rule.evaluate(profile) * 10) / 10;
  const evidence = rule.getEvidence(profile);
  const feedback = rule.getFeedback(profile, score);

  return {
    criterionId: rule.subCriterionId,
    score,
    maxScore: 10,
    evidence,
    feedback,
  };
}
